import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { FiMoreVertical, FiSearch, FiX } from 'react-icons/fi';
import { Spin } from 'antd';
import PageDataLoader from 'components/pageDataLoader';
import { getStudentList, updateStudentStatus, bulkUpdateStudentStatus, updateStudentBatch, assignCourseBulk } from 'actions/studentManagement';
import { setUserDetails } from 'actions/userManagementV2';
import { Notification } from 'services';
import ChangeBatchModal from '../../../pages/userManagementV2/changeBatchModal';
import EnrollCourseModal from '../../../pages/userManagementV2/enrollCourseModal';
import UserActionConfirmModal from '../../../pages/userManagementV3/localComponent/userActionConfirmModal';
import DownloadFileModal from './DownloadFileModal';
import PageSizeRange from './PageSizeRange';
import { scrollToTableTop } from '../utils/scrollToTableTop';
import { COLORS } from '../utils/constants';
import { fluid } from '../utils/responsive';
import FilterIcon from '../../../pages/userManagementV2/dashboardV3/filter-line.svg';
import SortIcon from '../../../pages/userManagementV2/dashboardV3/swap-half-vertical-reverse.svg';
import DownloadIcon from '../../../pages/userManagementV2/dashboardV3/download.svg';
import ArrowLeft from '../../../pages/userManagementV2/dashboardV3/arrow-single-left.svg';
import ArrowRight from '../../../pages/userManagementV2/dashboardV3/arrow-single-right.svg';
import illNoData from '../../../Assets/ill-no-data.svg';
import GridIcon from '../../../Assets/userManagementV3/gridIcon.svg';
import ListIcon from '../../../Assets/userManagementV3/listIcon.svg';

const formatContactPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return phone;
    const match = phone.trim().match(/^\+91\s*(\d+)$/);
    return match ? `+91 ${match[1]}` : phone;
};

const UNASSIGNED_STUDENT_EXPORT_FIELDS = [
    { id: 'name', label: 'Name', getValue: (row) => (row.name && row.name !== '—' ? row.name : 'NA') },
    {
        id: 'phone', label: 'Phone Number', getValue: (row) => {
            const p = formatContactPhone(row.phone);
            return p && p !== '—' ? p : 'NA';
        }
    },
    { id: 'email', label: 'Email ID', getValue: (row) => (row.email && row.email !== '—' ? row.email : 'NA') },
    { id: 'status', label: 'Status', getValue: (row) => (row.status && row.status !== '—' ? row.status : 'NA') },
    {
        id: 'joinedDate', label: 'Joined Date', getValue: (row) => {
            const d = (row.joinedOn || '').replace(/^Joined on\s*/i, '').trim();
            return d && d !== '—' ? d : 'NA';
        }
    },
];

const COLUMN_TEMPLATE = `${fluid(36)} minmax(${fluid(220)}, 1.5fr) minmax(${fluid(180)}, 1.4fr) minmax(${fluid(120)}, 1.2fr) ${fluid(100)}`;

const STATUS_PILLS = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
];

const SORT_PILLS = [
    { value: "alphabetic", label: "Alphabetic" },
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
];

const normalizeStatus = (value) => {
    const text = String(value || '').trim();
    if (!text) return 'Active';
    const normalized = text.toLowerCase();
    if (normalized === 'suspended') return 'Inactive';
    if (normalized === 'deleted') return 'Deleted';
    return 'Active';
};

const toApiStatus = (uiStatus) => {
    if (uiStatus === 'Inactive') return 'Suspended';
    if (uiStatus === 'Deleted') return 'Deleted';
    return 'Approved';
};

const formatDate = (value) => {
    if (!value) return 'Joined recently';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Joined recently';
    return `Joined on ${new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date)}`;
};

const resolveAvatarUrl = (image) => {
    if (!image || typeof image !== 'string') return '';
    const trimmed = image.trim();
    if (!trimmed || trimmed.toLowerCase() === 'default.png' || trimmed.toLowerCase().includes('default.png') || trimmed.toLowerCase() === 'default.jpg') {
        return '';
    }
    return trimmed;
};

const mapStudentRow = (student, index) => {
    const basicprofile = student?.basicprofile || {};
    const name = student?.name || basicprofile?.name || `Student ${index + 1}`;
    const phone = student?.phone || basicprofile?.phone || basicprofile?.mobile || '—';
    const email = student?.email || basicprofile?.email || '';
    const status = normalizeStatus(student?.status || student?.userStatus || basicprofile?.status || 'Active');
    const rawImage = student?.avatar || student?.image || basicprofile?.image || basicprofile?.avatar || '';
    const avatar = resolveAvatarUrl(rawImage);
    const joinedAt = student?.createdAt || student?.joinedAt || basicprofile?.createdAt || basicprofile?.joinedAt;
    const joinedOn = formatDate(joinedAt);

    return {
        id: student?.id ?? student?._id ?? student?.userId ?? `${name}-${index}`,
        name,
        phone,
        email,
        status,
        joinedAt,
        joinedOn,
        avatar,
        raw: student,
        courses: student?.courses || student?.course || [],
    };
};

const getStudentEnrollmentId = (studentObj, activeCourseId) => {
    if (!studentObj) return activeCourseId;
    const courses = Array.isArray(studentObj.courses)
        ? studentObj.courses
        : (Array.isArray(studentObj.course) ? studentObj.course : []);

    if (activeCourseId != null && courses.length > 0) {
        const matched = courses.find((c) => {
            const cId = c?.courseId ?? c?.course?.id ?? c?.id;
            return String(cId) === String(activeCourseId);
        });
        if (matched) {
            const enId = matched.enrollmentId ?? matched.id;
            if (enId) return enId;
        }
    }

    if (courses.length === 1) {
        const first = courses[0];
        const enId = first?.enrollmentId ?? first?.id;
        if (enId) return enId;
    }

    if (studentObj.enrollmentId) return studentObj.enrollmentId;
    if (studentObj.courseEnrollmentId) return studentObj.courseEnrollmentId;

    return activeCourseId;
};

const AVATAR_COLORS = ['#594099', '#1982C4', '#8AC926', '#F5A623', '#FF6B6B', '#4ECDC4', '#D97706', '#059669'];

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
};

const Checkbox = React.memo(({ checked, indeterminate = false, onChange, ariaLabel, disabled = false }) => {
    const filled = !disabled && (checked || indeterminate);
    return (
        <span
            role="checkbox"
            aria-checked={indeterminate ? 'mixed' : checked}
            aria-disabled={disabled}
            aria-label={ariaLabel}
            onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                if (disabled) return;
                onChange && onChange(e);
            }}
            style={{
                width: fluid(24),
                height: fluid(24),
                borderRadius: fluid(6),
                border: `1.5px solid ${disabled ? '#D0D0D0' : (filled ? '#594099' : '#B3B3B3')}`,
                backgroundColor: disabled ? '#EFEFEF' : (filled ? '#594099' : '#FFFFFF'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                flexShrink: 0,
            }}
        >
            {!disabled && (
                indeterminate ? (
                    <svg width="12" height="10" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4H9" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                ) : checked && (
                    <svg width="12" height="10" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )
            )}
        </span>
    );
});
Checkbox.displayName = 'Checkbox';

const Avatar = React.memo(({ name, image }) => {
    const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];
    if (image) {
        return (
            <img
                src={image}
                alt={name}
                style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
        );
    }
    return (
        <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: `${color}1A`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 700,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
Avatar.displayName = 'Avatar';

const PillGroup = React.memo(({ options, value, onChange }) => (
    <div style={{ display: "flex", gap: fluid(10), flexWrap: "wrap" }}>
        {options.map((opt) => {
            const isActive = opt.value === value;
            return (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    style={{
                        height: fluid(38),
                        padding: `0 ${fluid(18)}`,
                        borderRadius: fluid(1000),
                        border: isActive ? "none" : `1px solid #E6E6E6`,
                        backgroundColor: isActive ? "#594099" : "#FFFFFF",
                        color: isActive ? "#FFFFFF" : "#5A5A5A",
                        fontSize: fluid(14),
                        fontWeight: 500,
                        fontFamily: "Roboto, sans-serif",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    {opt.label}
                </button>
            );
        })}
    </div>
));
PillGroup.displayName = 'PillGroup';

const PopoverFooter = React.memo(({ onReset, onApply, disabled = false }) => (
    <>
        <div style={{ width: "100%", height: "1px", backgroundColor: "#E6E6E6", margin: `${fluid(20)} 0` }} />
        <div style={{ display: "flex", justifyContent: "space-between", gap: fluid(12) }}>
            <button
                type="button"
                onClick={disabled ? undefined : onReset}
                disabled={disabled}
                style={{
                    height: fluid(40),
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    boxSizing: "border-box",
                    padding: `0 ${fluid(20)}`,
                    borderRadius: fluid(10),
                    border: "1.5px solid #594099",
                    backgroundColor: "transparent",
                    color: "#594099",
                    fontSize: fluid(14),
                    fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                RESET
            </button>
            <button
                type="button"
                onClick={disabled ? undefined : onApply}
                disabled={disabled}
                style={{
                    height: fluid(40),
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    boxSizing: "border-box",
                    padding: `0 ${fluid(28)}`,
                    borderRadius: fluid(10),
                    border: "none",
                    backgroundColor: "#594099",
                    color: "#FFFFFF",
                    fontSize: fluid(14),
                    fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                APPLY
            </button>
        </div>
    </>
));
PopoverFooter.displayName = 'PopoverFooter';

// ---------- Grid view (same design as StudentListTable's StudentGridCard) ----------

const GRID_TEXT = { fontFamily: 'Roboto, sans-serif', letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

// 60px photo / initials for grid cards (falls back to initials if the photo fails).
const UnassignedGridAvatar = React.memo(({ name, image }) => {
    const [imgError, setImgError] = useState(false);
    const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];
    useEffect(() => { setImgError(false); }, [image]);
    if (image && !imgError) {
        return (
            <img src={image} alt={name} onError={() => setImgError(true)}
                style={{ width: fluid(60), height: fluid(60), borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        );
    }
    return (
        <div style={{ width: fluid(60), height: fluid(60), borderRadius: '50%', backgroundColor: `${color}1A`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Roboto, sans-serif', fontSize: fluid(24), fontWeight: 400, flexShrink: 0 }}>
            {getInitials(name)}
        </div>
    );
});
UnassignedGridAvatar.displayName = 'UnassignedGridAvatar';

const UnassignedGridCard = React.memo(({ row, checked, hovered, onHoverEnter, onHoverLeave, onToggleCheck, onToggleStatus, onDelete, onViewProfile, onAssign, assignLabel, emptyLabel, actionsDisabled = false }) => {
    const cardBg = checked ? '#EDE9F6' : '#FFFFFF';
    // Disabled while rows are selected — the header's bulk actions apply instead.
    const actionBtn = (color) => ({ border: 'none', background: 'transparent', padding: 0, fontSize: fluid(14), fontWeight: 700, cursor: actionsDisabled ? 'not-allowed' : 'pointer', opacity: actionsDisabled ? 0.4 : 1, color, whiteSpace: 'nowrap' });

    // Collapsed card stays in normal flow (row height never changes); the
    // hover card is an overlay drawn on top of it, like the student grid.
    return (
        <div style={{ width: '100%', position: 'relative', zIndex: hovered ? 100 : 1 }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', padding: fluid(20), boxSizing: 'border-box', backgroundColor: cardBg, borderRadius: fluid(24), boxShadow: checked ? 'none' : '4px 4px 10px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: fluid(12) }}>
                        <span onClick={() => onViewProfile(row)} style={{ ...GRID_TEXT, display: 'block', fontSize: fluid(16), color: '#191919', cursor: 'pointer' }}>{row.name}</span>
                        <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(7), fontSize: fluid(14), color: '#636363' }}>{formatContactPhone(row.phone)}</span>
                        <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(4), fontSize: fluid(14), color: '#636363', minHeight: fluid(16) }}>{row.email}</span>
                    </div>
                    <UnassignedGridAvatar name={row.name} image={row.avatar} />
                </div>
                <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(15), fontSize: fluid(16), color: '#AEAEAE' }}>{emptyLabel}</span>
            </div>

            {hovered && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 200, padding: fluid(20), boxSizing: 'border-box', display: 'flex', flexDirection: 'column', backgroundColor: cardBg, borderRadius: fluid(24), boxShadow: checked ? 'none' : '0 7px 22px rgba(0,0,0,0.12)' }}>
                    <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'flex-start' }}>
                        <Checkbox checked={checked} onChange={() => onToggleCheck(row.id)} ariaLabel={`Select ${row.name}`} />
                        <div style={{ minWidth: 0, flex: 1, paddingLeft: fluid(18), paddingRight: fluid(8) }}>
                            <span onClick={() => onViewProfile(row)} style={{ ...GRID_TEXT, display: 'block', fontSize: fluid(16), color: '#191919', cursor: 'pointer' }}>{row.name}</span>
                            <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(7), fontSize: fluid(14), color: '#636363' }}>{formatContactPhone(row.phone)}</span>
                            {row.email && <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(4), fontSize: fluid(14), color: '#636363' }}>{row.email}</span>}
                            <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(10), fontSize: fluid(14), color: '#AEAEAE' }}>{row.joinedOn}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <UnassignedGridAvatar name={row.name} image={row.avatar} />
                            <span onClick={() => onViewProfile(row)} style={{ marginTop: fluid(16), fontFamily: 'Roboto, sans-serif', color: '#1089FF', fontSize: fluid(14), fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>VIEW PROFILE</span>
                        </div>
                    </div>

                    <div style={{ marginTop: fluid(20), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: fluid(12) }}>
                        <button type="button" disabled={actionsDisabled} onClick={() => onAssign(row)} style={actionBtn('#594099')}>
                            {assignLabel}
                        </button>
                        <button type="button" disabled={actionsDisabled} onClick={() => onToggleStatus(row)} style={actionBtn(row.status === 'Active' ? '#FFA931' : '#008A67')}>
                            {row.status === 'Active' ? 'SUSPEND' : 'ACTIVATE'}
                        </button>
                        <button type="button" disabled={actionsDisabled} onClick={() => onDelete(row)} style={actionBtn('#FF414D')}>
                            DELETE
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});
UnassignedGridCard.displayName = 'UnassignedGridCard';

const UnassignedStudentsTable = React.memo(({
    isMobile = false,
    getStudentList,
    updateStudentStatus,
    bulkUpdateStudentStatus,
    updateStudentBatch,
    assignCourseBulk,
    setUserDetails,
    studentListTotal,
    courseId: propCourseId,
    courseType: propCourseType,
    courseName: propCourseName,
    onBatchAssigned,
}) => {
    const history = useHistory();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';

    const activeCourseId = propCourseId ?? location.state?.courseId ?? location.state?.course?.id;
    const currentContextKey = activeCourseId ? String(activeCourseId) : 'unassigned';
    const isUnassignedBatches = Boolean(activeCourseId && activeCourseId !== -1 && activeCourseId !== '-1');
    const activeCourseName = propCourseName || location.state?.courseName || location.state?.course?.courseName || location.state?.course?.title || "Classroom course";

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [openMenuId, setOpenMenuId] = useState(null);
    const [assignBatchModalData, setAssignBatchModalData] = useState(null);
    const [assignCourseModalOpen, setAssignCourseModalOpen] = useState(false);
    const [assignCourseTargetIds, setAssignCourseTargetIds] = useState(null);
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [allStatusCache, setAllStatusCache] = useState({ key: null, items: [] });
    const [confirmAction, setConfirmAction] = useState(null); // 'suspend' | 'activate' | 'delete'
    const [confirmScope, setConfirmScope] = useState(null); // { type: 'row', row } | { type: 'bulk' }
    const [confirmActionLoading, setConfirmActionLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [studentItems, setStudentItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("Active");
    const [itemsQuery, setItemsQuery] = useState({ search: '', status: 'Active' });
    const [sortBy, setSortBy] = useState("newest");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    // List / grid view (toggle next to the pager), like StudentListTable.
    const [viewMode, setViewMode] = useState('list');
    const [hoveredCardId, setHoveredCardId] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const [draftStatusFilter, setDraftStatusFilter] = useState(statusFilter);
    const [draftSortBy, setDraftSortBy] = useState(sortBy);

    const filterRef = useRef(null);
    const sortRef = useRef(null);
    const tableRootRef = useRef(null);
    const rowsRegionRef = useRef(null);

    // Changing page / rows per page: start the new page from its first row.
    const skipFirstPageScrollRef = useRef(true);
    useEffect(() => {
        if (skipFirstPageScrollRef.current) { skipFirstPageScrollRef.current = false; return; }
        scrollToTableTop(tableRootRef.current, rowsRegionRef.current);
    }, [page, limit]);
    const activeRequestIdRef = useRef(0);
    const fetchStudentsRef = useRef(null);
    const isInitialMountRef = useRef(true);

    const pageRef = useRef(page);
    pageRef.current = page;
    const limitRef = useRef(limit);
    limitRef.current = limit;
    const searchRef = useRef(search);
    searchRef.current = search;
    const statusFilterRef = useRef(statusFilter);
    statusFilterRef.current = statusFilter;

    const scrollTableIntoView = useCallback(() => {
        const el = tableRootRef.current;
        if (!el) return Promise.resolve();
        const { top } = el.getBoundingClientRect();
        if (Math.abs(top) < 4) return Promise.resolve();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return new Promise((resolve) => setTimeout(resolve, 450));
    }, []);

    const extractItemsFromResponse = (res) => {
        if (!res) return [];
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res)) return res;
        return [];
    };

    const fetchStudents = useCallback((callback, showLoader = false) => {
        const params = {
            page: 1,
            limit: 1000,
            hasBatch: false,
        };
        const activeCourseId = propCourseId ?? location.state?.courseId;
        if (activeCourseId && activeCourseId !== -1 && activeCourseId !== '-1') {
            params.courseId = activeCourseId;
        }
        const currStatus = statusFilterRef.current;
        if (currStatus !== 'All') params.status = toApiStatus(currStatus);
        const trimmed = searchRef.current.trim();
        if (trimmed) {
            params.search = trimmed;
        }
        if (sortBy === 'alphabetic') {
            params.sort = 'alphabetic';
            params.sortBy = 'alphabetic';
        } else if (sortBy === 'oldest') {
            params.sort = 'oldest';
            params.sortBy = 'oldest';
        } else {
            params.sort = 'newest';
            params.sortBy = 'newest';
        }

        const requestId = ++activeRequestIdRef.current;
        const isSearch = Boolean(trimmed);
        if (showLoader && !isSearch && studentItems.length === 0) {
            setIsLoading(true);
        }

        const safety = setTimeout(() => {
            if (requestId === activeRequestIdRef.current) {
                setIsLoading(false);
            }
        }, 5000);

        getStudentList?.(params, (response, error) => {
            if (requestId !== activeRequestIdRef.current) return;
            if (error || !response) {
                clearTimeout(safety);
                setIsLoading(false);
                callback?.();
                return;
            }
            const list = extractItemsFromResponse(response);
            const respTotal = Number(response?.total ?? response?.totalItems ?? response?.data?.total ?? response?.data?.totalItems ?? list.length ?? 0);
            if (respTotal > 0) {
                setTotalCount(respTotal);
            }

            if (list.length < respTotal && list.length > 0) {
                const pageSize = list.length;
                const totalPagesCount = Math.ceil(respTotal / pageSize);
                const pagePromises = [];
                for (let p = 2; p <= totalPagesCount; p++) {
                    pagePromises.push(
                        new Promise((resolve) => {
                            getStudentList?.({ ...params, page: p, limit: pageSize }, (pageRes) => {
                                resolve(extractItemsFromResponse(pageRes));
                            });
                        })
                    );
                }
                Promise.all(pagePromises).then((results) => {
                    if (requestId !== activeRequestIdRef.current) return;
                    clearTimeout(safety);
                    setIsLoading(false);
                    const combined = [...list, ...results.flat()];
                    setStudentItems(combined);
                    setItemsQuery({ search: trimmed, status: currStatus });
                    callback?.();
                }).catch(() => {
                    if (requestId !== activeRequestIdRef.current) return;
                    clearTimeout(safety);
                    setIsLoading(false);
                    setStudentItems(list);
                    setItemsQuery({ search: trimmed, status: currStatus });
                    callback?.();
                });
            } else {
                clearTimeout(safety);
                setIsLoading(false);
                setStudentItems(list);
                setItemsQuery({ search: trimmed, status: currStatus });
                callback?.();
            }
        });
    }, [getStudentList, propCourseId, location.state, studentItems.length, sortBy]);

    const fetchAllStatusStudents = useCallback(() => {
        const cId = propCourseId ?? location.state?.courseId ?? location.state?.course?.id;
        const contextKey = cId ? String(cId) : 'unassigned';
        const params = {
            page: 1,
            limit: 1000,
            sort: 'newest',
            sortBy: 'newest',
            hasBatch: false,
            skipRedux: true,
        };
        if (cId && cId !== -1 && cId !== '-1') {
            params.courseId = cId;
        }
        getStudentList?.(params, (response, error) => {
            if (error || !response) return;
            const list = extractItemsFromResponse(response);
            const respTotal = Number(response?.total ?? response?.totalItems ?? response?.data?.total ?? response?.data?.totalItems ?? 0);
            if (respTotal > 0) {
                setTotalCount(respTotal);
            }
            if (list.length < respTotal && list.length > 0) {
                const pageSize = list.length;
                const totalPagesCount = Math.ceil(respTotal / pageSize);
                const pagePromises = [];
                for (let p = 2; p <= totalPagesCount; p++) {
                    pagePromises.push(
                        new Promise((resolve) => {
                            getStudentList?.({ ...params, page: p, limit: pageSize }, (pageRes) => {
                                resolve(extractItemsFromResponse(pageRes));
                            });
                        })
                    );
                }
                Promise.all(pagePromises).then((results) => {
                    const combined = [...list, ...results.flat()];
                    setAllStatusCache({ key: contextKey, items: combined });
                }).catch(() => {
                    setAllStatusCache({ key: contextKey, items: list });
                });
            } else {
                setAllStatusCache({ key: contextKey, items: list });
            }
        });
    }, [propCourseId, location.state, getStudentList]);

    useEffect(() => {
        fetchAllStatusStudents();
    }, [fetchAllStatusStudents]);

    useEffect(() => {
        if (downloadModalOpen) {
            fetchAllStatusStudents();
        }
    }, [downloadModalOpen, fetchAllStatusStudents]);

    const refetchList = useCallback((callback) => {
        fetchStudents(callback, false);
        fetchAllStatusStudents();
    }, [fetchStudents, fetchAllStatusStudents]);

    useEffect(() => {
        fetchStudentsRef.current = fetchStudents;
    }, [fetchStudents]);

    useEffect(() => {
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            fetchStudents(undefined, true);
            return;
        }
        if (page !== 1) setPage(1);
        const timer = setTimeout(() => {
            fetchStudents(undefined, false);
        }, search.trim() ? 300 : 0);
        return () => clearTimeout(timer);
    }, [search, statusFilter, sortBy, fetchStudents]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const isFilterDisabled = statusFilter === "Active" && draftStatusFilter === "Active";
    const isSortDisabled = sortBy === "newest" && draftSortBy === "newest";

    useEffect(() => {
        if (!filterOpen) return undefined;
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setDraftStatusFilter(statusFilter);
                setFilterOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [filterOpen, statusFilter]);

    useEffect(() => {
        if (!sortOpen) return undefined;
        const handleClickOutside = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) {
                setDraftSortBy(sortBy);
                setSortOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [sortOpen, sortBy]);

    useEffect(() => {
        if (openMenuId === null) return undefined;
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [openMenuId]);

    const normalizedRows = useMemo(() => {
        const base = studentItems;
        return (Array.isArray(base) ? base : []).map((student, index) => mapStudentRow(student, index));
    }, [studentItems]);

    const allStatusRows = useMemo(() => (
        allStatusCache.key === currentContextKey
            ? (Array.isArray(allStatusCache.items) ? allStatusCache.items : []).map((student, index) => mapStudentRow(student, index))
            : []
    ), [allStatusCache, currentContextKey]);

    const isSearchPending = itemsQuery.search !== search.trim();
    const isStatusPending = itemsQuery.status !== statusFilter;
    const isPending = isSearchPending || isStatusPending;
    // While a search/status request is in flight, rows are filtered from the
    // all-students cache — once it's loaded, an empty result there is shown as
    // "no match" right away instead of a spinner on every keystroke.
    const showPendingLoader = isPending && allStatusCache.key !== currentContextKey;

    const openFilterPopover = useCallback(() => {
        setDraftStatusFilter(statusFilter);
        setFilterOpen((o) => !o);
    }, [statusFilter]);

    const applyFilterPopover = useCallback(() => {
        const nextStatus = draftStatusFilter;
        setStatusFilter(nextStatus);
        const hasCached = allStatusRows.some(row => nextStatus === 'All' || row.status === nextStatus);
        if (!hasCached) {
            setIsLoading(true);
        }
        setPage(1);
        setFilterOpen(false);
    }, [draftStatusFilter, allStatusRows]);

    const resetFilterPopover = useCallback(() => {
        setDraftStatusFilter("Active");
        setStatusFilter("Active");
        const hasCached = allStatusRows.some(row => row.status === 'Active');
        if (!hasCached) {
            setIsLoading(true);
        }
        setPage(1);
        setFilterOpen(false);
    }, [allStatusRows]);

    const openSortPopover = useCallback(() => {
        setDraftSortBy(sortBy);
        setSortOpen((o) => !o);
    }, [sortBy]);

    const applySortPopover = useCallback(() => {
        setSortBy(draftSortBy);
        setPage(1);
        setSortOpen(false);
    }, [draftSortBy]);

    const resetSortPopover = useCallback(() => {
        setDraftSortBy("newest");
        setSortBy("newest");
        setPage(1);
        setSortOpen(false);
    }, []);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        const searchPending = itemsQuery.search !== search.trim();
        const statusPending = itemsQuery.status !== statusFilter;
        const pending = searchPending || statusPending;

        let next = (pending && allStatusRows.length > 0) ? [...allStatusRows] : [...normalizedRows];
        next = next.filter((row) => {
            const matchesStatus = statusFilter === 'All' || row.status === statusFilter;
            const matchesQuery = !query
                || (row.name || '').toLowerCase().includes(query)
                || (row.phone || '').toLowerCase().includes(query)
                || (row.email || '').toLowerCase().includes(query);
            return matchesStatus && matchesQuery;
        });

        if (sortBy === 'alphabetic') {
            next.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortBy === 'oldest') {
            next.sort((a, b) => {
                const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : new Date((a.joinedOn || '').replace(/^Joined on /, '')).getTime();
                const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : new Date((b.joinedOn || '').replace(/^Joined on /, '')).getTime();
                return (dateA || 0) - (dateB || 0);
            });
        } else {
            next.sort((a, b) => {
                const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : new Date((a.joinedOn || '').replace(/^Joined on /, '')).getTime();
                const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : new Date((b.joinedOn || '').replace(/^Joined on /, '')).getTime();
                return (dateB || 0) - (dateA || 0);
            });
        }
        return next;
    }, [normalizedRows, allStatusRows, itemsQuery, search, statusFilter, sortBy]);

    const exportFields = useMemo(() => {
        if (isUnassignedBatches) {
            return [
                { id: 'name', label: 'Name', getValue: (row) => (row.name && row.name !== '—' ? row.name : 'NA') },
                {
                    id: 'phone', label: 'Phone Number', getValue: (row) => {
                        const p = formatContactPhone(row.phone);
                        return p && p !== '—' ? p : 'NA';
                    }
                },
                { id: 'email', label: 'Email ID', getValue: (row) => (row.email && row.email !== '—' ? row.email : 'NA') },
                { id: 'course', label: 'Course', getValue: () => (activeCourseName && activeCourseName !== 'Classroom course' ? activeCourseName : 'NA') },
                { id: 'status', label: 'Status', getValue: (row) => (row.status && row.status !== '—' ? row.status : 'NA') },
                {
                    id: 'joinedDate', label: 'Joined Date', getValue: (row) => {
                        const d = (row.joinedOn || '').replace(/^Joined on\s*/i, '').trim();
                        return d && d !== '—' ? d : 'NA';
                    }
                },
            ];
        }
        return UNASSIGNED_STUDENT_EXPORT_FIELDS;
    }, [isUnassignedBatches, activeCourseName]);

    const toggleSelectRow = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        if (openMenuId !== null && selectedIds.has(openMenuId)) {
            setOpenMenuId(null);
        }
    }, [selectedIds, openMenuId]);

    const bulkSetStatus = useCallback((status, callback) => {
        const ids = Array.from(selectedIds);
        if (!ids.length) {
            callback?.();
            return;
        }
        bulkUpdateStudentStatus?.(ids, status, (response, error) => {
            const hasApiNotification = Boolean(response?.show || response?.data?.show);
            if (error) {
                const hasErrorNotification = Boolean(error?.show || error?.data?.show);
                if (!hasErrorNotification) {
                    const errorMsg = (typeof error === 'string' ? error : (error?.message || error?.error)) || (status === 'Deleted' ? 'Failed to delete user.' : status === 'Approved' ? 'Failed to activate user.' : 'Failed to suspend user.');
                    Notification.error('Error', errorMsg);
                }
            } else {
                const resData = response?.data ?? response;
                const failed = resData?.failed || response?.failed || [];
                const succeeded = resData?.succeeded || response?.succeeded || [];

                if (failed.length > 0 && succeeded.length === 0) {
                    if (!hasApiNotification) {
                        Notification.error(
                            'Error',
                            failed.map((f) => f.reason || (f.userId ? `User ${f.userId} failed` : 'Action failed')).join(' | ') || (status === 'Deleted' ? 'Failed to delete user.' : status === 'Approved' ? 'Failed to activate user.' : 'Failed to suspend user.')
                        );
                    }
                } else {
                    if (failed.length > 0) {
                        Notification.warning(
                            'Some students could not be updated',
                            failed.map((f) => `User ${f.userId}: ${f.reason}`).join(' | ')
                        );
                    }
                    if (!hasApiNotification && status !== 'Suspended' && status !== 'Deleted') {
                        if (status === 'Approved') {
                            Notification.success('Success', 'User activated successfully.');
                        }
                    }
                }
            }
            setSelectedIds(new Set());
            refetchList();
            callback?.();
        });
    }, [selectedIds, bulkUpdateStudentStatus, refetchList]);

    const bulkSuspend = useCallback((callback) => bulkSetStatus('Suspended', callback), [bulkSetStatus]);
    const bulkActivate = useCallback((callback) => bulkSetStatus('Approved', callback), [bulkSetStatus]);
    const bulkDelete = useCallback((callback) => bulkSetStatus('Deleted', callback), [bulkSetStatus]);

    const toggleRowStatus = useCallback((row, callback) => {
        setOpenMenuId(null);
        const nextStatus = toApiStatus(row.status === 'Active' ? 'Inactive' : 'Active');
        updateStudentStatus?.(row.id, nextStatus, (response, error) => {
            if (error) {
                const hasErrorNotification = Boolean(error?.show || error?.data?.show);
                if (!hasErrorNotification) {
                    Notification.error('Error', error?.message || (nextStatus === 'Suspended' ? 'Failed to suspend user.' : 'Failed to activate user.'));
                }
            }
            refetchList();
            callback?.();
        });
    }, [updateStudentStatus, refetchList]);

    const deleteRow = useCallback((row, callback) => {
        setOpenMenuId(null);
        updateStudentStatus?.(row.id, 'Deleted', (response, error) => {
            if (error) {
                const hasErrorNotification = Boolean(error?.show || error?.data?.show);
                if (!hasErrorNotification) {
                    Notification.error('Error', error?.message || 'Failed to delete user.');
                }
            }
            refetchList();
            callback?.();
        });
    }, [updateStudentStatus, refetchList]);

    const requestToggleStatus = useCallback((row) => {
        setOpenMenuId(null);
        setConfirmAction(row?.status === 'Active' ? 'suspend' : 'activate');
        setConfirmScope({ type: 'row', row });
    }, []);

    const requestDeleteRow = useCallback((row) => {
        setOpenMenuId(null);
        setConfirmAction('delete');
        setConfirmScope({ type: 'row', row });
    }, []);

    const requestBulkDelete = useCallback(() => {
        if (!selectedIds.size) return;
        setConfirmAction('delete');
        setConfirmScope({ type: 'bulk' });
    }, [selectedIds]);

    const requestBulkSuspend = useCallback(() => {
        if (!selectedIds.size) return;
        setConfirmAction('suspend');
        setConfirmScope({ type: 'bulk' });
    }, [selectedIds]);

    const requestBulkActivate = useCallback(() => {
        if (!selectedIds.size) return;
        setConfirmAction('activate');
        setConfirmScope({ type: 'bulk' });
    }, [selectedIds]);

    const closeConfirmModal = useCallback(() => {
        if (confirmActionLoading) return;
        setConfirmAction(null);
        setConfirmScope(null);
    }, [confirmActionLoading]);

    const handleConfirmAction = useCallback(() => {
        if (!confirmScope || confirmActionLoading) return;
        setConfirmActionLoading(true);

        const onDone = () => {
            setConfirmActionLoading(false);
            setConfirmAction(null);
            setConfirmScope(null);
        };

        if (confirmScope.type === 'row') {
            if (confirmAction === 'delete') {
                deleteRow(confirmScope.row, onDone);
            } else {
                toggleRowStatus(confirmScope.row, onDone);
            }
        } else {
            if (confirmAction === 'delete') {
                bulkDelete(onDone);
            } else if (confirmAction === 'activate') {
                bulkActivate(onDone);
            } else {
                bulkSuspend(onDone);
            }
        }
    }, [confirmScope, confirmAction, confirmActionLoading, bulkDelete, bulkSuspend, bulkActivate, deleteRow, toggleRowStatus]);

    const handleViewProfile = (row) => {
        setOpenMenuId(null);
        setUserDetails?.(row);
        history.push(`${basePath}/student-profile`, {
            returnTo: `${location.pathname}${location.search}`,
            returnState: location.state,
        });
    };

    // payloads: one per course block in the Assign course modal. The API takes
    // a single courseId, so courses are assigned one after another, followed
    // by one summary message and one refetch.
    const handleAssignCourseBulk = async (payloads, modalCallback) => {
        const courses = Array.isArray(payloads) ? payloads : [payloads];
        const targetIds = assignCourseTargetIds && assignCourseTargetIds.length > 0
            ? assignCourseTargetIds
            : Array.from(selectedIds);
        const studentIds = targetIds.map(Number);
        if (!studentIds.length || !courses.length || courses.some((p) => !p?.courseId)) {
            modalCallback?.(false);
            return;
        }

        const toBulkPayload = (payload) => {
            const effectiveCourseType = (payload?.courseType === 'live' ? 'course' : payload?.courseType) || 'course';
            const bulkPayload = {
                courseType: effectiveCourseType,
                courseId: Number(payload.courseId),
                students: studentIds,
            };
            if (effectiveCourseType === 'course' && payload?.standardId && payload.standardId !== -1 && payload.standardId !== '-1') {
                bulkPayload.standardId = Number(payload.standardId);
            }
            return bulkPayload;
        };
        const assignOne = (payload) => new Promise((resolve) => {
            assignCourseBulk?.(toBulkPayload(payload), (response, error) => resolve({ response, error }));
        });

        const results = [];
        for (const payload of courses) {
            results.push(await assignOne(payload));
        }

        if (courses.length > 1) {
            const failureReasons = [];
            let fullyFailed = 0;
            results.forEach(({ response, error }) => {
                if (error) {
                    fullyFailed += 1;
                    failureReasons.push(error?.message || 'Something went wrong');
                    return;
                }
                const resData = response?.data ?? response;
                const succeeded = resData?.succeeded || [];
                const failed = resData?.failed || [];
                failed.forEach((f) => failureReasons.push(f.reason || `User ${f.userId} failed`));
                if (succeeded.length === 0 && failed.length > 0) fullyFailed += 1;
            });

            // Nothing went through: keep the modal open so it can be retried.
            if (fullyFailed === courses.length) {
                modalCallback?.(false);
                Notification.error('Failed to assign course', failureReasons.join(', ') || 'Something went wrong');
                return;
            }
            modalCallback?.(true);
            if (failureReasons.length === 0) {
                Notification.success('Success', `${courses.length} courses assigned successfully to ${studentIds.length} student${studentIds.length > 1 ? 's' : ''}`);
            } else {
                Notification.warning('Partially Assigned', `Some courses could not be assigned: ${failureReasons.join(', ')}`);
            }
            setSelectedIds(new Set());
            setAssignCourseTargetIds(null);
            refetchList();
            return;
        }

        // Single course: same messages as before.
        {
            const { response, error } = results[0];
            if (error) {
                modalCallback?.(false);
                Notification.error('Failed to assign course', error?.message || 'Something went wrong');
                return;
            }

            const resData = response?.data ?? response;
            const succeeded = resData?.succeeded || [];
            const failed = resData?.failed || [];

            if (succeeded.length > 0 && failed.length === 0) {
                modalCallback?.(true);
                Notification.success('Success', `Course assigned successfully to ${succeeded.length} student${succeeded.length > 1 ? 's' : ''}`);
            } else if (succeeded.length > 0 && failed.length > 0) {
                modalCallback?.(true);
                Notification.warning(
                    'Partially Assigned',
                    `Assigned to ${succeeded.length} student(s). Failed for ${failed.length}: ${failed.map((f) => f.reason || `User ${f.userId}`).join(', ')}`
                );
            } else if (failed.length > 0) {
                modalCallback?.(false);
                Notification.error(
                    'Failed to assign course',
                    failed.map((f) => f.reason || `User ${f.userId} failed`).join(', ')
                );
            } else {
                modalCallback?.(true);
                Notification.success('Success', 'Course assigned successfully');
            }

            setSelectedIds(new Set());
            setAssignCourseTargetIds(null);
            refetchList();
        }
    };

    const handleAssignBatch = (payload, callback) => {
        if (!assignBatchModalData || !activeCourseId) {
            callback(false);
            return;
        }
        const studentIds = assignBatchModalData.studentIds || [];
        if (!studentIds.length) {
            callback(false);
            return;
        }

        const standardId = Number(payload.standardId) || payload.standardId;

        const findStudent = (sId) => {
            return studentItems.find((s) => {
                const id = s?.id ?? s?._id ?? s?.userId;
                return String(id) === String(sId);
            });
        };

        if (studentIds.length === 1) {
            const sId = studentIds[0];
            const studentObj = findStudent(sId);
            const enrollmentId = getStudentEnrollmentId(studentObj, activeCourseId);

            updateStudentBatch?.(sId, enrollmentId, { standardId }, (response, error) => {
                if (response && !error) {
                    Notification.success("Success", "Batch assigned successfully");
                    callback(true);
                    setAssignBatchModalData(null);
                    setSelectedIds(new Set());
                    refetchList();
                    onBatchAssigned?.();
                } else {
                    Notification.error("Error", error?.show?.message || error?.message || "Failed to assign batch");
                    callback(false);
                }
            });
        } else {
            const promises = studentIds.map((sId) => {
                const studentObj = findStudent(sId);
                const enrollmentId = getStudentEnrollmentId(studentObj, activeCourseId);

                return new Promise((resolve) => {
                    updateStudentBatch?.(sId, enrollmentId, { standardId }, (response, error) => {
                        resolve({ sId, success: !error && !!response, error });
                    });
                });
            });

            Promise.all(promises).then((results) => {
                const failed = results.filter((r) => !r.success);
                if (failed.length === 0) {
                    Notification.success("Success", `${results.length} students assigned to batch successfully`);
                    callback(true);
                } else if (failed.length < results.length) {
                    Notification.warning("Warning", `${results.length - failed.length} students assigned, ${failed.length} failed`);
                    callback(true);
                } else {
                    const errMsg = failed[0]?.error?.show?.message || failed[0]?.error?.message || "Failed to assign batch";
                    Notification.error("Error", errMsg);
                    callback(false);
                }
                setAssignBatchModalData(null);
                setSelectedIds(new Set());
                refetchList();
                onBatchAssigned?.();
            });
        }
    };

    const hasSearchOrFilter = Boolean(search.trim() || statusFilter !== 'Active');
    const total = hasSearchOrFilter
        ? filteredRows.length
        : (filteredRows.length > 0 ? filteredRows.length : (totalCount || Number(studentListTotal || 0)));
    const totalPages = Math.max(1, Math.ceil(total / limit));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const displayedRows = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredRows.slice(start, start + limit);
    }, [filteredRows, page, limit]);

    const allChecked = displayedRows.length > 0 && displayedRows.every((row) => selectedIds.has(row.id));
    const someChecked = !allChecked && (displayedRows.some((row) => selectedIds.has(row.id)) || selectedIds.size > 0);
    const startIndex = total ? (page - 1) * limit + 1 : 0;
    const endIndex = total ? Math.min(page * limit, total) : 0;
    const canGoPrev = page > 1 && !isLoading;
    const canGoNext = page < totalPages && !isLoading;

    const isInactiveFilter = statusFilter === 'Inactive' || (selectedIds.size > 0 && filteredRows.filter((r) => selectedIds.has(r.id)).length > 0 && filteredRows.filter((r) => selectedIds.has(r.id)).every((r) => r.status === 'Inactive'));

    const toggleSelectAll = () => {
        if (displayedRows.length === 0) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) {
                displayedRows.forEach((row) => next.delete(row.id));
            } else {
                displayedRows.forEach((row) => next.add(row.id));
            }
            return next;
        });
    };

    const pagerNode = (
        <div style={{
            display: "flex",
            alignItems: "center",
            gap: fluid(6),
            height: fluid(40),
            boxSizing: "border-box",
            padding: `0 ${fluid(8)}`,
            borderRadius: fluid(1000),
            backgroundColor: "#F7F7F7",
            fontSize: fluid(18),
            lineHeight: fluid(24),
            fontWeight: 400,
            color: "#666666",
            whiteSpace: "nowrap",
        }}>
            <button
                type="button"
                disabled={!canGoPrev}
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                style={{
                    width: fluid(24),
                    height: fluid(24),
                    border: "none",
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: canGoPrev ? "pointer" : "not-allowed",
                    opacity: canGoPrev ? 1 : 0.4,
                    padding: 0,
                }}
            >
                <img src={ArrowLeft} alt="Previous page" style={{ width: fluid(24), height: fluid(24) }} />
            </button>
            <PageSizeRange
                label={total ? `${startIndex}-${endIndex} of ${total.toLocaleString()}` : '0-0 of 0'}
                pageSize={limit}
                onChange={handleLimitChange}
            />
            <button
                type="button"
                disabled={!canGoNext}
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                style={{
                    width: fluid(24),
                    height: fluid(24),
                    border: "none",
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: canGoNext ? "pointer" : "not-allowed",
                    opacity: canGoNext ? 1 : 0.4,
                    padding: 0,
                }}
            >
                <img src={ArrowRight} alt="Next page" style={{ width: fluid(24), height: fluid(24) }} />
            </button>
        </div>
    );

    return (
        <div ref={tableRootRef} style={{
            backgroundColor: "#FFFFFF",
            borderRadius: fluid(24),
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            fontFamily: "Roboto, sans-serif",
            position: 'relative',
        }}>
            {/* The in-table Spin covers the empty state; the overlay only shows while refetching over existing rows. */}
            <PageDataLoader visible={isLoading && filteredRows.length > 0} />
            <div style={{
                position: isMobile ? "static" : "sticky",
                top: isMobile ? undefined : 0,
                zIndex: 20,
                backgroundColor: "#FFFFFF",
                borderRadius: `${fluid(24)} ${fluid(24)} 0 0`,
                flexShrink: 0,
            }}>
                <div style={{ padding: isMobile ? "18px 18px 0" : `${fluid(24)} ${fluid(28)} 0`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
                    {/* Toolbar Row */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: fluid(14),
                        paddingBottom: fluid(18),
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(2) }}>
                            <span style={{ fontSize: fluid(20), fontWeight: 700, color: "#191919" }}>
                                Unassigned students <span style={{ color: "#AEAEAE", fontWeight: 500 }}>({total.toLocaleString()})</span>
                            </span>
                            {/* Grid view shows the count in "Select all (N selected)". */}
                            {selectedIds.size > 0 && viewMode === 'list' && (
                                <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(14), fontWeight: 500, color: '#636363' }}>
                                    Selected {selectedIds.size} {selectedIds.size === 1 ? 'student' : 'students'}
                                </span>
                            )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: fluid(10), flexWrap: "wrap" }}>
                            {selectedIds.size > 0 ? (
                                <div style={{ display: "flex", alignItems: "center", gap: fluid(12), flexWrap: "wrap" }}>
                                    {isUnassignedBatches ? (
                                        <button
                                            type="button"
                                            onClick={() => setAssignBatchModalData({ studentIds: Array.from(selectedIds) })}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: fluid(40),
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: "border-box",
                                                borderRadius: fluid(12),
                                                border: "2px solid #594099",
                                                backgroundColor: "transparent",
                                                color: "#594099",
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            Assign batch
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setAssignCourseTargetIds(Array.from(selectedIds));
                                                setAssignCourseModalOpen(true);
                                            }}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: fluid(40),
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: "border-box",
                                                borderRadius: fluid(12),
                                                border: "2px solid #594099",
                                                backgroundColor: "transparent",
                                                color: "#594099",
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            Assign course
                                        </button>
                                    )}
                                    {isInactiveFilter ? (
                                        <button
                                            type="button"
                                            onClick={requestBulkActivate}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: fluid(40),
                                                lineHeight: 1,
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: "border-box",
                                                borderRadius: fluid(12),
                                                border: "2px solid #008A67",
                                                backgroundColor: "transparent",
                                                color: "#008A67",
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                                textAlign: "center",
                                            }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                                Activate
                                            </span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={requestBulkSuspend}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: fluid(40),
                                                lineHeight: 1,
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: "border-box",
                                                borderRadius: fluid(12),
                                                border: "2px solid #FFA931",
                                                backgroundColor: "transparent",
                                                color: "#FFA931",
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                                textAlign: "center",
                                            }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                                Suspend
                                            </span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={requestBulkDelete}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: fluid(40),
                                            lineHeight: 1,
                                            padding: `0 ${fluid(24)}`,
                                            boxSizing: "border-box",
                                            borderRadius: fluid(12),
                                            border: "2px solid #FF414D",
                                            backgroundColor: "transparent",
                                            color: "#FF414D",
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                            textAlign: "center",
                                        }}
                                    >
                                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                            Delete
                                        </span>
                                    </button>
                                    {pagerNode}
                                </div>
                            ) : (
                                <>
                                    {/* Search input */}
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: fluid(12),
                                        padding: `0 ${fluid(16)}`,
                                        height: fluid(40),
                                        borderRadius: fluid(1000),
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #B3B3B3",
                                        width: isMobile ? "220px" : fluid(400),
                                        boxSizing: "border-box",
                                    }}>
                                        <FiSearch size={18} color={COLORS.placeholderGray || "#B3B3B3"} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => {
                                                setSearch(e.target.value);
                                                setPage(1);
                                            }}
                                            onFocus={scrollTableIntoView}
                                            placeholder="Search by name, phone No., email"
                                            style={{
                                                border: "none",
                                                outline: "none",
                                                width: "100%",
                                                fontSize: fluid(16),
                                                color: "#191919",
                                                fontFamily: "Roboto, sans-serif"
                                            }}
                                        />
                                        {search && (
                                            <button
                                                type="button"
                                                onClick={() => setSearch("")}
                                                title="Clear search"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: "none",
                                                    background: "transparent",
                                                    padding: 0,
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <FiX size={18} color={COLORS.placeholderGray || "#B3B3B3"} strokeWidth={1.5} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Filter controls */}
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        height: fluid(40),
                                        borderRadius: fluid(1000),
                                        backgroundColor: "#F1F1F1",
                                        boxSizing: "border-box",
                                    }}>
                                        <div ref={filterRef} style={{ position: "relative" }}>
                                            <button
                                                type="button"
                                                onClick={() => { scrollTableIntoView().then(openFilterPopover); }}
                                                style={{
                                                    width: fluid(46),
                                                    height: fluid(40),
                                                    borderRadius: `${fluid(1000)} ${fluid(8)} ${fluid(8)} ${fluid(1000)}`,
                                                    border: "none",
                                                    backgroundColor: filterOpen ? COLORS.pillBg : "#F1F1F1",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: `${fluid(10)} ${fluid(10)} ${fluid(10)} ${fluid(16)}`,
                                                    boxSizing: "border-box",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <img src={FilterIcon} alt="Filter" style={{ width: fluid(20), height: fluid(20) }} />
                                            </button>

                                            {filterOpen && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: fluid(48),
                                                    right: 0,
                                                    minWidth: fluid(360),
                                                    backgroundColor: "#FFFFFF",
                                                    borderRadius: fluid(20),
                                                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                                                    padding: fluid(20),
                                                    zIndex: 30,
                                                    boxSizing: "border-box",
                                                }}>
                                                    <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 700, color: "#191919", marginBottom: fluid(12) }}>
                                                        Status
                                                    </div>
                                                    <PillGroup options={STATUS_PILLS} value={draftStatusFilter} onChange={setDraftStatusFilter} />

                                                    <PopoverFooter disabled={isFilterDisabled} onReset={resetFilterPopover} onApply={applyFilterPopover} />
                                                </div>
                                            )}
                                        </div>

                                        <div ref={sortRef} style={{ position: "relative" }}>
                                            <button
                                                type="button"
                                                onClick={() => { scrollTableIntoView().then(openSortPopover); }}
                                                style={{ width: fluid(40), height: fluid(40), border: "none", backgroundColor: sortOpen ? COLORS.pillBg : "#F1F1F1", borderRadius: fluid(8), display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                            >
                                                <img src={SortIcon} alt="Sort" style={{ width: fluid(20), height: fluid(20) }} />
                                            </button>

                                            {sortOpen && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: fluid(48),
                                                    right: 0,
                                                    minWidth: fluid(360),
                                                    backgroundColor: "#FFFFFF",
                                                    borderRadius: fluid(20),
                                                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                                                    padding: fluid(20),
                                                    zIndex: 30,
                                                    boxSizing: "border-box",
                                                }}>
                                                    <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 700, color: "#191919", marginBottom: fluid(12) }}>
                                                        Sort by
                                                    </div>
                                                    <PillGroup options={SORT_PILLS} value={draftSortBy} onChange={setDraftSortBy} />

                                                    <PopoverFooter disabled={isSortDisabled} onReset={resetSortPopover} onApply={applySortPopover} />
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setDownloadModalOpen(true)}
                                            title="Export"
                                            style={{
                                                width: fluid(46),
                                                height: fluid(40),
                                                borderRadius: `${fluid(8)} ${fluid(1000)} ${fluid(1000)} ${fluid(8)}`,
                                                border: "none",
                                                backgroundColor: "#F1F1F1",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                padding: 0,
                                            }}
                                        >
                                            <img src={DownloadIcon} alt="Export" style={{ width: fluid(20), height: fluid(20) }} />
                                        </button>
                                    </div>

                                    {/* Pagination */}
                                    {pagerNode}

                                    <button
                                        type="button"
                                        onClick={() => { setViewMode((m) => (m === 'list' ? 'grid' : 'list')); setHoveredCardId(null); }}
                                        title={viewMode === 'list' ? 'Grid view' : 'List view'}
                                        style={{ width: fluid(40), height: fluid(40), borderRadius: fluid(8), border: 'none', outline: 'none', appearance: 'none', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: fluid(4), boxSizing: 'border-box', boxShadow: 'none', cursor: 'pointer', flexShrink: 0 }}
                                    >
                                        <img src={viewMode === 'list' ? GridIcon : ListIcon} alt={viewMode === 'list' ? 'Grid view' : 'List view'} style={{ width: fluid(32), height: fluid(32) }} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {viewMode === 'grid' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: fluid(12), height: fluid(42), boxSizing: 'border-box', margin: isMobile ? '0 18px' : `0 ${fluid(24)}`, flexShrink: 0 }}>
                        <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleSelectAll} ariaLabel="Select all" disabled={displayedRows.length === 0} />
                        <span style={{ fontSize: fluid(14), fontWeight: 400, color: '#636363', fontFamily: 'Roboto, sans-serif' }}>
                            Select all{selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ''}
                        </span>
                    </div>
                )}

                {/* Table Header */}
                {viewMode === 'list' && (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: COLUMN_TEMPLATE,
                    alignItems: "center",
                    gap: fluid(12),
                    margin: isMobile ? "0 18px" : `0 ${fluid(25)}`,
                    padding: `${fluid(10)} ${fluid(20)}`,
                    backgroundColor: "#F4F4F4",
                    borderRadius: `${fluid(16)} ${fluid(16)} 0 0`,
                    flexShrink: 0,
                }}>
                    <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleSelectAll} ariaLabel="Select all" disabled={displayedRows.length === 0} />
                    <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Student</span>
                    <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Contact</span>
                    <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Status</span>
                    <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#A2A2A2", fontFamily: "Roboto, sans-serif", textAlign: "right" }}>Actions</span>
                </div>
                )}
            </div>

            {/* Table Rows Body Area */}
            <div ref={rowsRegionRef} className="dv2-rows-region" style={viewMode === 'grid' ? {
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                margin: isMobile ? "12px 18px 16px" : `${fluid(12)} ${fluid(24)} ${fluid(16)}`,
                boxSizing: "border-box",
            } : {
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: isMobile ? "auto" : "visible",
                margin: isMobile ? "0 18px 16px" : `0 ${fluid(25)} ${fluid(16)}`,
                backgroundColor: "#FFFFFF",
                borderRadius: `0 0 ${fluid(16)} ${fluid(16)}`,
                borderLeft: "1px solid #E6E6E6",
                borderRight: "1px solid #E6E6E6",
                borderBottom: "1px solid #E6E6E6",
                boxSizing: "border-box",
            }}>
                {/* Grid: at least 358px per card, then stretch so the row fills the full width. */}
                {viewMode === 'grid' && displayedRows.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(auto-fill, minmax(${fluid(358)}, 1fr))`, gap: fluid(24), paddingBottom: fluid(24), boxSizing: 'border-box' }}>
                        {displayedRows.map((row) => (
                            <UnassignedGridCard
                                key={row.id}
                                row={row}
                                checked={selectedIds.has(row.id)}
                                hovered={hoveredCardId === row.id}
                                onHoverEnter={() => setHoveredCardId(row.id)}
                                onHoverLeave={() => setHoveredCardId(null)}
                                onToggleCheck={toggleSelectRow}
                                onToggleStatus={requestToggleStatus}
                                onDelete={requestDeleteRow}
                                onViewProfile={handleViewProfile}
                                onAssign={(r) => {
                                    if (isUnassignedBatches) {
                                        setAssignBatchModalData({ studentIds: [r.id] });
                                    } else {
                                        setAssignCourseTargetIds([r.id]);
                                        setAssignCourseModalOpen(true);
                                    }
                                }}
                                assignLabel={isUnassignedBatches ? 'ASSIGN BATCH' : 'ASSIGN COURSE'}
                                emptyLabel={isUnassignedBatches ? 'No batch assigned' : 'No course assigned'}
                                actionsDisabled={selectedIds.size > 0}
                            />
                        ))}
                    </div>
                )}
                {viewMode === 'list' && displayedRows.map((row, index) => {
                    const isRowSelected = selectedIds.has(row.id);
                    const isLast = index === displayedRows.length - 1;
                    return (
                        <div key={row.id} style={{ position: "relative" }}>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: COLUMN_TEMPLATE,
                                alignItems: "center",
                                gap: fluid(12),
                                minHeight: "72px",
                                padding: `0 ${fluid(20)}`,
                                boxSizing: "border-box",
                            }}>
                                {/* Checkbox column */}
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSelectRow(row.id);
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '100%',
                                        alignSelf: 'stretch',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Checkbox
                                        checked={isRowSelected}
                                        onChange={(e) => {
                                            if (e && e.stopPropagation) e.stopPropagation();
                                            toggleSelectRow(row.id);
                                        }}
                                        ariaLabel={`Select ${row.name}`}
                                    />
                                </div>

                                {/* Student column */}
                                <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                                    <Avatar name={row.name} image={row.avatar} />
                                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                        <span
                                            onClick={() => handleViewProfile(row)}
                                            style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif", cursor: "pointer" }}>
                                            {row.name}
                                        </span>
                                        <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#AEAEAE", whiteSpace: "nowrap", fontFamily: "Roboto, sans-serif" }}>
                                            {row.joinedOn}
                                        </span>
                                    </div>
                                </div>

                                {/* Contact column */}
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                    <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                                        {row.phone}
                                    </span>
                                    {row.email && (
                                        <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#AEAEAE", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                                            {row.email}
                                        </span>
                                    )}
                                </div>

                                {/* Status column */}
                                <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                    <span style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "22px",
                                        padding: "0 10px",
                                        borderRadius: "4px",
                                        backgroundColor: row.status === "Active" ? "#ECFDF5" : row.status === "Deleted" ? "#FEE2E2" : "#FFFBEB",
                                        border: `1px solid ${row.status === "Active" ? "#34D399" : row.status === "Deleted" ? "#FCA5A5" : "#FBBF24"}`,
                                        color: row.status === "Active" ? "#059669" : row.status === "Deleted" ? "#B91C1C" : "#D97706",
                                        fontSize: "12px",
                                        fontWeight: row.status === "Active" ? 600 : 500,
                                        lineHeight: "18px",
                                        boxSizing: "border-box",
                                        whiteSpace: "nowrap",
                                        fontFamily: "Roboto, sans-serif"
                                    }}>
                                        {row.status}
                                    </span>
                                </div>

                                {/* Actions column */}
                                <div style={{ position: "relative", justifySelf: "end" }}>
                                    <button
                                        type="button"
                                        disabled={isRowSelected}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isRowSelected) return;
                                            setOpenMenuId(prev => prev === row.id ? null : row.id);
                                        }}
                                        style={{
                                            width: fluid(44),
                                            height: fluid(44),
                                            border: "none",
                                            borderRadius: fluid(12),
                                            backgroundColor: openMenuId === row.id ? "#F1F1F1" : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: isRowSelected ? "not-allowed" : "pointer",
                                            opacity: isRowSelected ? 0.4 : 1,
                                        }}
                                    >
                                        <FiMoreVertical size={24} color="#666666" strokeWidth={2} />
                                    </button>

                                    {openMenuId === row.id && (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                position: "absolute",
                                                top: fluid(48),
                                                right: 0,
                                                minWidth: fluid(150),
                                                backgroundColor: "#FFFFFF",
                                                borderRadius: fluid(16),
                                                boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                                                padding: fluid(8),
                                                zIndex: 30,
                                            }}
                                        >
                                            <div
                                                onClick={() => handleViewProfile(row)}
                                                className="staff-row-menu-item"
                                                style={{ display: "flex", alignItems: "center", padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: "#5A5A5A", cursor: "pointer" }}
                                            >
                                                View
                                            </div>
                                            {isUnassignedBatches ? (
                                                <div
                                                    onClick={() => {
                                                        setOpenMenuId(null);
                                                        setAssignBatchModalData({ studentIds: [row.id] });
                                                    }}
                                                    className="staff-row-menu-item"
                                                    style={{ display: "flex", alignItems: "center", padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: "#594099", cursor: "pointer" }}
                                                >
                                                    Assign batch
                                                </div>
                                            ) : (
                                                <div
                                                    onClick={() => {
                                                        setOpenMenuId(null);
                                                        setAssignCourseTargetIds([row.id]);
                                                        setAssignCourseModalOpen(true);
                                                    }}
                                                    className="staff-row-menu-item"
                                                    style={{ display: "flex", alignItems: "center", padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: "#594099", cursor: "pointer" }}
                                                >
                                                    Assign course
                                                </div>
                                            )}
                                            <div
                                                onClick={() => requestToggleStatus(row)}
                                                className="staff-row-menu-item"
                                                style={{ display: "flex", alignItems: "center", padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: row.status === 'Active' ? "#D97706" : "#008A67", cursor: "pointer" }}
                                            >
                                                {row.status === 'Active' ? 'Suspend' : 'Activate'}
                                            </div>
                                            <div
                                                onClick={() => requestDeleteRow(row)}
                                                className="staff-row-menu-item"
                                                style={{ display: "flex", alignItems: "center", padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: "#FF0010", cursor: "pointer" }}
                                            >
                                                Delete
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!isLast && (
                                <div style={{
                                    position: "absolute",
                                    left: fluid(24),
                                    right: fluid(24),
                                    bottom: 0,
                                    height: "1px",
                                    backgroundColor: "#E6E6E6",
                                }} />
                            )}
                        </div>
                    );
                })}

                {(isLoading && filteredRows.length === 0) || (showPendingLoader && filteredRows.length === 0) ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `${fluid(48)} 0`,
                        width: '100%',
                        // Fill the rows region so the spinner sits in the middle of the table body.
                        height: '100%',
                        boxSizing: 'border-box',
                        minHeight: fluid(260),
                    }}>
                        <Spin size="large" />
                    </div>
                ) : filteredRows.length === 0 && !isLoading && !showPendingLoader ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `${fluid(48)} 0`,
                        width: '100%',
                        // Fill the rows region so the empty state sits in the middle of the table body.
                        height: '100%',
                        boxSizing: 'border-box',
                        minHeight: fluid(260),
                    }}>
                        <img src={illNoData} style={{ width: fluid(220) }} alt="No unassigned students" />
                        <span style={{
                            fontSize: fluid(18),
                            fontWeight: 500,
                            color: '#636363',
                            marginTop: fluid(16),
                            fontFamily: 'Roboto, sans-serif'
                        }}>
                            No unassigned students match your search/filters
                        </span>
                    </div>
                ) : null}


            </div>

            <ChangeBatchModal
                visible={!!assignBatchModalData}
                onClose={() => setAssignBatchModalData(null)}
                title="Assign batch"
                courseName={activeCourseName}
                courseId={activeCourseId}
                onAssign={handleAssignBatch}
            />

            <EnrollCourseModal
                visible={assignCourseModalOpen}
                onClose={() => {
                    setAssignCourseModalOpen(false);
                    setAssignCourseTargetIds(null);
                }}
                onEnroll={handleAssignCourseBulk}
                isBulk={true}
                allowMultipleCourses
                selectedCount={assignCourseTargetIds?.length || selectedIds.size || 1}
            />

            <UserActionConfirmModal
                open={!!confirmScope}
                onCancel={closeConfirmModal}
                onConfirm={handleConfirmAction}
                action={confirmAction || 'suspend'}
                loading={confirmActionLoading}
                isBulk={confirmScope?.type === 'bulk' ? selectedIds.size > 1 : false}
            />

            <DownloadFileModal
                open={downloadModalOpen}
                onClose={() => setDownloadModalOpen(false)}
                fields={exportFields}
                rows={filteredRows}
                allRows={allStatusRows.length ? allStatusRows : normalizedRows}
                entityLabel="students"
                fileName="Unassigned_Students_List"
                sheetName="Unassigned Students"
                getDate={(row) => row.joinedAt || row.joinedOn}
            />
        </div>
    );
});

UnassignedStudentsTable.displayName = 'UnassignedStudentsTable';

const mapStateToProps = (state) => ({
    studentListTotal: state?.studentManagement?.studentListTotal || 0,
});

const mapDispatchToProps = (dispatch) => ({
    getStudentList: (params, callback) => dispatch(getStudentList(params, callback)),
    updateStudentStatus: (userId, status, callback) => dispatch(updateStudentStatus(userId, status, callback)),
    bulkUpdateStudentStatus: (userIds, status, callback) => dispatch(bulkUpdateStudentStatus(userIds, status, callback)),
    updateStudentBatch: (userId, enrollmentId, params, callback) => dispatch(updateStudentBatch(userId, enrollmentId, params, callback)),
    assignCourseBulk: (params, callback) => dispatch(assignCourseBulk(params, callback)),
    setUserDetails: (row) => dispatch(setUserDetails(row)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UnassignedStudentsTable);
